import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Grid from '@mui/material/Grid';
import SettingsIcon from '@mui/icons-material/Settings';
import AddBoxIcon from '@mui/icons-material/AddBox';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Button, Dialog, MenuItem, Select, paginationClasses } from '@mui/material';
import { SelectChangeEvent } from '@mui/material';
import { useState, useEffect } from 'react';
import store, { useAppDispatch, useAppSelector } from '../store';
import { setActiveOrganization } from '../store/reducers/organizationSlice';
import { IOrganization } from '../models/organization';
import { getOrganizations } from '../utilities/api-utils';
import OrganizationDialog from './dialogs/organization-dialog';
import { setActiveBrand, setActiveLanguage } from '../store/reducers/brandSlice';
import BrandDialog from './dialogs/brand-dialog';
import { getAllBrands } from '../utilities/brand-api-utils';
import { Brand } from '../models/brand';

interface HeaderProps {
  title: string,
  headerTabs?: string[],
  isBranding?: boolean,
  onTabChange?(arg: string): void
}

function Header({ title, headerTabs, isBranding = false, onTabChange}: HeaderProps) {
  const tabs =  headerTabs ? headerTabs : [''];

  const [selectedTab, setSelectedTab] = useState<string>(tabs[0]);
  const [organizations, setOrganizations] = useState<IOrganization[]>([]);
  const [dialog, setDialog] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);

  const [brands, setBrands] = useState<Brand[]>([]);
  const [languages, setLanguages] = useState<Array<string>>([]);
  
  const { activeOrganization } = useAppSelector(store => store.organization);
  const activeBrand = useAppSelector(store => store.brand.activeBrand);
  const activeLanguage = useAppSelector(store => store.brand.activeLanguage);
  const allLanguages = useAppSelector(store => store.brand.allLanguages);
  const dispatch = useAppDispatch();
  const { isSearching } = useAppSelector(store => store.cabinet);

  const organizationSelected = (e: SelectChangeEvent) => {
    dispatch(setActiveOrganization(e.target.value));
  };

  const brandSelected = async (e: SelectChangeEvent) => {
    dispatch(setActiveBrand(e.target.value));
  };

  const languageSelected = (e: SelectChangeEvent) => {
    dispatch(setActiveLanguage(e.target.value));
  };

  const tabSelected = (i: string) => {
    if (onTabChange) {
      onTabChange(i);
    }
    setSelectedTab(i);
  };

  const setDialogState = (open: boolean, update?: boolean) => {
    setDialog(open);
    if (update) {
      setRefresh(!refresh);
    }
  };

  useEffect(() => {
    const getAllOrganizations = async () => {
      const orgs = await getOrganizations((store.getState().authorization.cloudKey));
      const sorted = orgs.data.sort((a, b) => {
        const textA = a.name.toUpperCase();
        const textB = b.name.toUpperCase();
        return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
      });
      setOrganizations(sorted);
      if (!activeOrganization) {
        dispatch(setActiveOrganization(sorted[0].id));
      }
    };

    const getS3Brands = async () => {
      const brandList = await getAllBrands(store.getState().authorization.cloudKey);

      if (brandList.data) {
        const brandsList = brandList.data;
        setBrands(brandsList);

        if (!activeBrand) {
          dispatch(setActiveBrand(brandsList[0].id));
        }
      }
    };

    if (isBranding) {
      getS3Brands();
    } else {
      getAllOrganizations();
    }
  }, [refresh]);

  return (
    <React.Fragment>
      <AppBar color="primary" position="sticky" elevation={0} sx={{height: '50px'}} />
      <AppBar
        component="div"
        color="primary"
        position="static"
        elevation={0}
        sx={{ zIndex: 0 }}>
        <Toolbar>
          <Grid container alignItems="center" spacing={1}>
            <Grid item xs>
              <Typography color="inherit" variant="h3" component="h1" sx={{ textAlign: 'start' }}>
                {title}
              </Typography>
            </Grid>
            <Grid 
              display='flex' 
              flexDirection='row' 
              justifyContent='center' 
              alignItems='center' 
              sx={{ 
                width: 'auto', 
                height: 'auto'
              }}>
              {!isBranding ?
              <Tooltip title='Select Organization' placement='top'>
                <Select
                  disabled={isSearching}
                  sx={{
                    color: 'white', 
                    width: '300px', 
                    height: '40px', 
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'white !important',
                    },
                    '& .MuiSelect-iconOutlined': {
                      color: 'white'
                    }
                  }} 
                  value={
                    (activeOrganization === undefined ||
                    activeOrganization === null ||
                    organizations.length === 0)
                    ? ''
                    : activeOrganization
                  }
                  onChange={organizationSelected}>
                  {organizations.map((org, index) => (
                    <MenuItem 
                      key={index}
                      value={org.id}>
                        {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </Tooltip> 
              :
              <Tooltip title='Select Brand' placement='top'>
                <Select
                  sx={{
                    color: 'white', 
                    width: '300px', 
                    height: '40px', 
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'white !important',
                    },
                    '& .MuiSelect-iconOutlined': {
                      color: 'white'
                    }
                  }} 
                  value={
                    (activeBrand === undefined ||
                    activeBrand === null ||
                    brands.length === 0)
                    ? ''
                    : activeBrand
                  }
                  onChange={brandSelected}>
                  {brands.map((brand, index) => (
                    <MenuItem 
                      key={index}
                      value={brand.id}>
                        {brand.id}
                    </MenuItem>
                  ))}
                </Select>
              </Tooltip>
              }
              <Button
                sx={{
                  marginLeft: '10px', 
                  padding: '0', 
                  minWidth: '0px', 
                  color: 'white', 
                  width: '40px', 
                  height: '40px', 
                  '&:hover': {
                    backgroundColor: '#121F35',
                    opacity: '.7'
                  }
                }}
                variant='text'
                onClick={() => setDialog(true)}>
                {isBranding ?
                <Tooltip title='Add Brand' placement='top'>
                  <AddBoxIcon sx={{
                    fontSize: '2rem', 
                    padding: '0'
                    }} />
                </Tooltip> :
                <Tooltip title='Add/Edit Organizations' placement='top'>
                  <SettingsIcon sx={{
                    fontSize: '2rem', 
                    padding: '0'
                    }} />
                </Tooltip>
                }
              </Button>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      <AppBar component="div" position="static" elevation={0} sx={{ zIndex: 0, display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
        <Tabs value={selectedTab} textColor="inherit">
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab} value={tab} onClick={() => tabSelected(tab)} />
          ))}
        </Tabs>
        {(isBranding && selectedTab === 'Text') ?
        <Tooltip title='Select Language' placement='left'>
          <Select 
            sx={{
              color: 'white', 
              width: '100px', 
              height: '40px',
              marginRight: '74px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'white !important',
              },
              '& .MuiSelect-iconOutlined': {
                color: 'white'
              }
            }} 
            value={
              (activeLanguage === undefined ||
              activeLanguage === null ||
              allLanguages.length === 0)
              ? ''
              : activeLanguage
            }
            onChange={languageSelected}>
            {allLanguages.map((lang, index) => (
              <MenuItem 
                key={index}
                value={lang}>
                  {lang}
              </MenuItem>
            ))}
          </Select>
        </Tooltip>
        : <></>
        }
      </AppBar>
      <Dialog
        sx={{
          width: '100%'
        }}
        fullWidth
        maxWidth={isBranding ? 'sm' : 'lg'}
        open={dialog}
        onClose={() => setDialog(false)}>
        {isBranding ? 
          <BrandDialog brands={brands} setDialog={setDialogState}/>
          :
          <OrganizationDialog orgs={organizations} setDialog={setDialogState} />
        }
      </Dialog>
    </React.Fragment>
  );
}

export default Header;
